import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../config";
import { X, Loader2, FileText, PiggyBank, Wallet } from "lucide-react";

const METHODS = { datafono: "Datáfono", transferencia: "Transferencia", efectivo: "Efectivo", bizum: "Bizum", otro: "Otro" };
const DEPOSIT_STATUSES = { pendiente: "Pendiente de cobrar", retenida: "Retenida (cobrada)", devuelta: "Devuelta", parcial: "Retenida parcial (daños)" };
export const DEPOSIT_BADGE = {
  pendiente: ["Fianza pendiente", "bg-zinc-200 text-topacio-faded"],
  retenida: ["Fianza retenida", "bg-brand text-topacio-ink"],
  devuelta: ["Fianza devuelta", "bg-topacio-green text-white"],
  parcial: ["Fianza parcial", "bg-topacio-red text-white"],
};

// Botón que descarga/abre el contrato PDF generado al momento (con sesión admin)
export function ContractButton({ bookingRef, headers }) {
  const [busy, setBusy] = useState(false);
  return (
    <button data-testid={`contract-btn-${bookingRef}`} title="Generar contrato de alquiler (PDF)"
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          const r = await axios.get(`${API}/admin/bookings/${bookingRef}/contract`, { headers, responseType: "blob" });
          const url = URL.createObjectURL(r.data);
          window.open(url, "_blank");
          setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch { toast.error("No se pudo generar el contrato"); } finally { setBusy(false); }
      }}
      className="w-8 h-8 bg-white border-2 border-topacio-ink rounded-lg flex items-center justify-center hover:bg-brand transition-colors">
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
    </button>
  );
}

// Liquidación de comisión partner: fecha + método de pago
export function CommissionModal({ booking, headers, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [paidAt, setPaidAt] = useState(today);
  const [method, setMethod] = useState("transferencia");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await axios.patch(`${API}/admin/bookings/${booking.ref}/commission`,
        { paid: true, paid_at: paidAt, method }, { headers });
      toast.success(`Comisión de ${booking.commission_eur}€ liquidada`);
      onSaved();
      onClose();
    } catch { toast.error("Error al liquidar"); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center p-4" data-testid="commission-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative card-brutal w-full max-w-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-topacio-ink flex items-center gap-2"><Wallet className="w-5 h-5 text-topacio-red" /> Liquidar comisión</h3>
          <button data-testid="commission-modal-close" onClick={onClose} className="text-topacio-faded hover:text-topacio-ink"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm font-semibold text-topacio-faded mb-4">
          {booking.partner_name || booking.partner_code} · reserva {booking.ref} · <span className="text-topacio-red font-extrabold">{booking.commission_eur}€</span>
        </p>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Fecha de liquidación</label>
            <input data-testid="commission-date" type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} className="input-brutal mt-1" />
          </div>
          <div>
            <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Método de pago</label>
            <select data-testid="commission-method" value={method} onChange={(e) => setMethod(e.target.value)} className="input-brutal mt-1">
              {Object.entries(METHODS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
          <button data-testid="commission-confirm-btn" onClick={save} disabled={busy || !paidAt} className="btn-primary w-full !py-3">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Marcar como liquidada
          </button>
        </div>
      </div>
    </div>
  );
}

// Gestión de la fianza: importe, método y estado de devolución
export function DepositModal({ booking, headers, onClose, onSaved }) {
  const [status, setStatus] = useState(booking.deposit_status || "pendiente");
  const [method, setMethod] = useState(booking.deposit_method || "datafono");
  const [amount, setAmount] = useState(booking.deposit_amount ?? "");
  const [cancelFee, setCancelFee] = useState(booking.cancellation_fee ?? "");
  const [retained, setRetained] = useState(booking.deposit_retained || "");
  const [note, setNote] = useState(booking.deposit_note || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await axios.patch(`${API}/admin/bookings/${booking.ref}/deposit`, {
        status, method, amount: amount === "" ? null : parseFloat(amount),
        retained: status === "parcial" ? (parseFloat(retained) || 0) : null, note,
        cancellation_fee: cancelFee === "" ? null : parseFloat(cancelFee),
      }, { headers });
      toast.success("Fianza y cancelación actualizadas");
      onSaved();
      onClose();
    } catch { toast.error("Error al guardar la fianza"); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center p-4" data-testid="deposit-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative card-brutal w-full max-w-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-topacio-ink flex items-center gap-2"><PiggyBank className="w-5 h-5 text-topacio-red" /> Fianza y cancelación · {booking.ref}</h3>
          <button data-testid="deposit-modal-close" onClick={onClose} className="text-topacio-faded hover:text-topacio-ink"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Estado</label>
            <select data-testid="deposit-status" value={status} onChange={(e) => setStatus(e.target.value)} className="input-brutal mt-1">
              {Object.entries(DEPOSIT_STATUSES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Importe (€)</label>
              <input data-testid="deposit-amount" type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="input-brutal mt-1" />
            </div>
            <div>
              <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Método</label>
              <select data-testid="deposit-method" value={method} onChange={(e) => setMethod(e.target.value)} className="input-brutal mt-1">
                {Object.entries(METHODS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Penalización por cancelación (€)</label>
            <input data-testid="cancellation-fee-input" type="number" min="0" step="0.01" value={cancelFee} onChange={(e) => setCancelFee(e.target.value)} className="input-brutal mt-1" />
            <p className="text-[10px] text-topacio-faded font-semibold mt-1.5">Se guarda en la reserva y sale en el contrato PDF.</p>
          </div>
          {status === "parcial" && (
            <div>
              <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Importe retenido por daños (€)</label>
              <input data-testid="deposit-retained" type="number" min="0" step="0.01" value={retained} onChange={(e) => setRetained(e.target.value)} className="input-brutal mt-1" />
            </div>
          )}
          <div>
            <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Nota (daños, incidencias…)</label>
            <textarea data-testid="deposit-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="input-brutal mt-1" />
          </div>
          <button data-testid="deposit-save-btn" onClick={save} disabled={busy} className="btn-primary w-full !py-3">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Guardar fianza
          </button>
        </div>
      </div>
    </div>
  );
}
